import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { verifyOTP, resendOTP } from "../../redux/Slice/Settings/twoFactorSlice";
import { setAccessToken } from "../../utils/tokenManager";
import { initializeSocket } from "../../utils/socketHandler";
import { resetRefreshTokenFailure } from "../../utils/getAuthToken";

const OTPDialog = ({ open, onClose, email, onSuccess }) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (open) {
      setOtp("");
      setError("");
      setResendSuccess(false);
      setCountdown(60); // 60 seconds cooldown for resend
    }
  }, [open]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6); // Only numbers, max 6 digits
    setOtp(value);
    setError("");
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError("Please enter a 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await dispatch(verifyOTP({ email, otp })).unwrap();
      
      // If OTP verification succeeds, the result contains accessToken and user data
      if (result.accessToken) {
        // Store access token in memory (not localStorage - XSS protection)
        const expiresIn = result.expiresIn || 900;
        setAccessToken(result.accessToken, expiresIn);
        resetRefreshTokenFailure();

        // Store only minimal non-sensitive data in localStorage (name for display purposes)
        // All sensitive data (IDs, tokens, workspaces) should come from Redux state
        const safeUserData = {
          name: result.name || result.email || 'User'
        };
        localStorage.setItem("user", JSON.stringify(safeUserData));

        // Clear any old dummy/test data
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");

        // Update Redux auth state by dispatching the fulfilled login action
        // This ensures the auth state is properly updated after OTP verification
        dispatch({
          type: "auth/loginUser/fulfilled",
          payload: {
            ...result,
            token: result.accessToken, // Add legacy token property for compatibility
          }
        });

        // Initialize socket if needed
        const userId = result._id || result.userId;
        if (userId && result.accessToken) {
          initializeSocket(userId, result.accessToken);
        }

        onSuccess(result);
        onClose();
      }
    } catch (err) {
      // Extract error message properly - handle both string and object errors
      const errorMessage = typeof err === 'string' 
        ? err 
        : err?.message || err?.error?.message || "Invalid OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setResendLoading(true);
    setError("");
    setResendSuccess(false);

    try {
      await dispatch(resendOTP(email)).unwrap();
      setResendSuccess(true);
      setCountdown(60); // Reset countdown
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      // Extract error message properly - handle both string and object errors
      const errorMessage = typeof err === 'string' 
        ? err 
        : err?.message || err?.error?.message || "Failed to resend OTP. Please try again.";
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Two-Factor Authentication
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            We've sent a 6-digit OTP code to <strong>{email}</strong>. Please
            enter it below to complete your login.
          </Typography>

          {resendSuccess && (
            <Alert severity="success" sx={{ marginBottom: 1 }}>
              New OTP has been sent to your email
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ marginBottom: 1 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Enter OTP"
            value={otp}
            onChange={handleOTPChange}
            fullWidth
            inputProps={{
              maxLength: 6,
              style: {
                textAlign: "center",
                fontSize: "24px",
                letterSpacing: "8px",
                fontFamily: "monospace",
              },
            }}
            placeholder="000000"
            disabled={loading}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter" && otp.length === 6) {
                handleVerify();
              }
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Didn't receive the code?
            </Typography>
            <Button
              onClick={handleResend}
              disabled={resendLoading || countdown > 0}
              size="small"
              sx={{ textTransform: "none" }}
            >
              {resendLoading
                ? "Sending..."
                : countdown > 0
                ? `Resend in ${countdown}s`
                : "Resend OTP"}
            </Button>
          </Box>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontStyle: "italic" }}
          >
            The OTP will expire in 5 minutes
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={loading || otp.length !== 6}
          sx={{
            backgroundColor: "#a0eaa3",
            color: "#000000",
            "&:hover": {
              backgroundColor: "#8ad28d",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(160, 234, 163, 0.3)",
              color: "rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} />
              <span>Verifying...</span>
            </Box>
          ) : (
            "Verify"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OTPDialog;

